import { useState } from 'react';
import { useAppStateContext } from '../../state/AppContextProvider';
import { Box, Stack, Typography, Menu, MenuItem } from '@mui/material';
import BattleCard from '../BattleCard/BattleCard';

// You can use a hook or context for mobile detection, e.g. useMediaQuery
import useMediaQuery from '@mui/material/useMediaQuery';
import { getCurrentOrchestration, OrchestrationType } from '../../common/models';

interface AnswerFooterButtonsProps {
    onButtonClick: (label: string) => void;
}

const AnswerFooterButtons = ({ onButtonClick }: AnswerFooterButtonsProps) => {
    const { state, dispatch, clearCurrentChat, getChatHistory, exportChat, setMaxStep, setCurrentOrchestrationType, setShouldAddContinueOption } = useAppStateContext();
    const orchState = state.currentOrchState;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [battleCardOpen, setBattleCardOpen] = useState(false);
    const [exportingType, setExportingType] = useState<string | null>(null);

    // Detect mobile
    const isPhone = useMediaQuery('(max-width:600px)');

    
    const currentOrch = getCurrentOrchestration(state.currentOrchState);
        
    // Determine if "Continue Negotiation Prep" should be added
    const shouldAddContinueOption = state.shouldAddContinueOption;
    
    let handoffButtons = currentOrch?.handoffRecommendation?.split('|').map(q => q.trim()).filter(Boolean) || [];

    // Add "Continue Negotiation Prep" option if shouldAddContinueOption is true
    if (shouldAddContinueOption) {
        handoffButtons = ['Continue Negotiation Prep', ...handoffButtons];
    }

    const nextPromptButtons = orchState?.nextPrompt?.split('|').map(q => q.trim()).filter(Boolean) || [];

    // Filter out "None" and disabled agent options
    const filteredHandoffButtons = handoffButtons.filter(label => label.trim().toLowerCase() !== "none" && label.trim().toLowerCase() !== "negotiationoneagent");
    const filteredNextPromptButtons = nextPromptButtons.filter(label => label.trim().toLowerCase() !== "none" && label.trim().toLowerCase() !== "negotiationoneagent");

    // Use filtered lists for mobile menu and desktop buttons
    const mainButtons = filteredHandoffButtons.slice(0, 3);
    const coachingButtons = filteredNextPromptButtons.slice(0, 2);

    // Combine all buttons for mobile dropdown
    const allMenuButtons = [
        ...filteredHandoffButtons.map(label => ({ label, type: 'handoff' })),
        ...filteredNextPromptButtons.map(label => ({ label, type: 'coach' }))
    ];

    const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDropdownClose = () => {
        setAnchorEl(null);
    };

    // Add this new function to handle menu item clicks
    const handleMenuItemClick = async (callback: () => void | Promise<void>) => {
        // Close menu first to remove aria-hidden
        setAnchorEl(null);
        
        // Small delay to ensure menu is fully closed before executing callback
        setTimeout(async () => {
            await callback();
        }, 0);
    };

    // Add this function to mimic newChat from MuiLayout and fully reset conversation context
    const triggerNewChat = async () => {

        // Reset max step on continuing negotiation prep
        setMaxStep(0); 

        //switch orchestration type to continue negotiation
        setCurrentOrchestrationType(OrchestrationType.StartNegotiation);

        // Clear active document first
        dispatch({
            type: 'SET_ACTIVE_DOCUMENT',
            payload: null
        });
        
        // Clear current chat - this resets messages, sessionId, agent, and orchestration state
        await clearCurrentChat();
        
        // Get fresh chat history to ensure clean state
        await getChatHistory();
    };

    // Handle export for One Sheet and Prep Sheet
    const handleExportPDF = async (exportType: string) => {
        try {
            setExportingType(exportType);
            const sessionId = state.currentConversation.sessionId;
            if (!sessionId) {
                const { DownloadNotificationService } = await import('../../services/downloadNotificationService');
                DownloadNotificationService.error(
                    'Export Error',
                    'No active session found. Please start a conversation first.'
                );
                return;
            }
            const abortController = new AbortController();
            await exportChat(exportType, sessionId, abortController.signal);
        } catch (err) {
            const { DownloadNotificationService } = await import('../../services/downloadNotificationService');
            DownloadNotificationService.error(
                'Export Failed',
                'There was an error exporting the PDF. Please try again.'
            );
            console.error(err);
        } finally {
            setExportingType(null);
        }
    };

    return (
        <Box 
            sx={{ 
                mt: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                width: 'fit-content',
                marginLeft: 'auto',
                marginRight: 0
            }}
        >
            {/* Always show dropdown for both mobile and desktop */}
            {(allMenuButtons.length > 0) && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            background: '#fff',
                            borderRadius: '40px',
                            boxShadow: 'none',
                            height: '25px',
                            minWidth: '0',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            px: 0,
                            py: 0,
                            '&:hover': {
                                background: 'linear-gradient(90deg, #b8091a 0%, #e50c20 100%)',
                            },
                        }}
                        onClick={handleDropdownClick}
                    >
                        <Box
                            sx={{
                                background: 'linear-gradient(90deg, #e50c20 0%, #b8091a 100%)',
                                width: '2%',
                                minWidth: '20px',
                                height: '100%',
                                borderRadius: '40px 0 0 40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        />
                        <Typography
                            component="p"
                            id="tools"
                            sx={{
                                fontSize: '.85rem',
                                fontWeight: 400,
                                pl: 2,
                                pr: 4,
                                py: 1.5,
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                color: 'text.primary',
                                bgcolor: 'background.paper',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #b8091a 0%, #e50c20 100%)',
                                    color: '#fff'
                                },
                            }}
                        >
                            Tools
                        </Typography>
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleDropdownClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            sx: isPhone
                                ? {
                                    mt: 1,
                                    minWidth: '85vw',
                                    maxWidth: '85vw',
                                    width: '85vw',
                                    left: 0,
                                    right: 0,
                                    transform: 'none',
                                    bottom: 0,
                                    position: 'fixed',
                                    borderWidth: '1px',
                                    borderStyle: 'solid',
                                    borderColor: '#e50c20'
                                }
                                : {
                                    borderRadius: '24px',
                                    // boxShadow: 'none',
                                    mt: 1,
                                    // bgcolor: 'background.paper',
                                    borderWidth: '1px',
                                    borderStyle: 'solid',
                                    borderColor: '#e50c20'
                                }
                        }}
                    >
                        {[...allMenuButtons].map(({ label, type }, idx) => (
                            <MenuItem
                                key={idx}
                                disabled={exportingType !== null}
                                onClick={async () => {
                                    // Use the new handler instead of direct execution
                                    handleMenuItemClick(async () => {
                                        const labelLower = label.trim().toLowerCase();
                                        
                                        // Handle special tool actions that don't send messages
                                        if (labelLower === "start a new negotiation" || labelLower === "home") {
                                            await triggerNewChat();
                                        } else if (labelLower === "generate battle card") {
                                            setBattleCardOpen(true);
                                        } else if (labelLower === "generate prep sheet") {
                                            await handleExportPDF("prepsheet");
                                        } else if (type === 'coach') {
                                            onButtonClick(`Hey Coach, ${label}`);
                                        } else {

                                            if(labelLower === "continue negotiation prep") {
                                                // Reset max step on continuing negotiation prep
                                                setMaxStep(0); 

                                                // Specify not to ladd continue negotiation option
                                                setShouldAddContinueOption(false);

                                                //switch orchestration type to continue negotiation
                                                setCurrentOrchestrationType(OrchestrationType.ContinueNegotiation);
                                            }
                                            onButtonClick(label);
                                        }
                                    });
                                }}
                                sx={{
                                    fontSize: '.85rem',
                                    whiteSpace: 'normal',
                                    width: { xs: '100%', sm: 'auto' },
                                    maxWidth: { xs: '100%', sm: 220 },
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #e50c20 0%, #b8091a 100%)',
                                        color: '#fff'
                                    },
                                    justifyContent: isPhone ? 'center' : 'flex-start',
                                    opacity: exportingType !== null ? 0.6 : 1
                                }}
                            >
                                {exportingType === 'prepsheet' && label.trim().toLowerCase() === 'generate prep sheet' 
                                    ? 'Exporting...' 
                                    : label}
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
            
            {/* Battle Card Dialog */}
            <BattleCard 
                open={battleCardOpen}
                onClose={() => setBattleCardOpen(false)}
            />
        </Box>
    );
};
export default AnswerFooterButtons;
// In your answer component, render <AnswerFooterButtons /> below the response box